import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useUserId } from "@/hooks/useUserId";
import { Instagram, Check, ArrowLeft, Loader2 } from "lucide-react";
import { extractInstagramUsername, validateInstagramInput } from "@/utils/instagramUsername";
import { preloadAuthImage } from "@/hooks/useAuthImage";
import { supabase } from "@/integrations/supabase/client";
import { formatFollowerCount } from "@/utils/instagram";
import { logEdgeFunctionError } from "@/utils/sentry";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import VideoTutorialHint from "@/components/onboarding/VideoTutorialHint";

const STEPS = [
  { id: 1, label: 'Profil' },
  { id: 2, label: 'Branża' },
  { id: 3, label: 'Inspiracje' },
  { id: 4, label: 'Konkurencja' },
  { id: 5, label: 'Płatność' },
  { id: 6, label: 'Start' },
];

interface VerifiedProfile {
  username: string;
  full_name: string;
  profile_pic_url: string;
  followers_count: number;
  is_verified: boolean;
}

const InstagramSetup = () => {
  const [avatarError, setAvatarError] = useState(false);
  const [avatarBlobUrl, setAvatarBlobUrl] = useState<string | null>(null);
  const [instagramLink, setInstagramLink] = useState<string>("");
  const [validationError, setValidationError] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verifiedProfile, setVerifiedProfile] = useState<VerifiedProfile | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const userId = useUserId();

  // We deliberately do NOT pre-create a placeholder profile row here.
  // `instagram_username` is NOT NULL in the schema, so any pre-creation would
  // require a sentinel like 'pending_setup' which then leaks downstream into
  // scraping and analytics. Instead, the row is created/updated in
  // `handleConfirmContinue` once the user has a verified, real username.
  // Auth.tsx falls back to redirecting users with a missing profile to this
  // same step, so nothing is lost by deferring the insert.

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Revoke blob URL on unmount to prevent memory leak
  useEffect(() => {
    return () => {
      if (avatarBlobUrl) URL.revokeObjectURL(avatarBlobUrl);
    };
  }, [avatarBlobUrl]);

  useEffect(() => {
    const saved = localStorage.getItem('onboarding_instagram');
    if (saved) {
      setInstagramLink(saved);
    }
  }, []);

  const isValidUsername = (input: string): boolean => {
    return validateInstagramInput(input) === "";
  };

  useEffect(() => {
    const error = validateInstagramInput(instagramLink);
    setValidationError(error);
  }, [instagramLink]);

  const handleContinueClick = async () => {
    if (!userId) {
      toast({
        title: "Wymagane uwierzytelnienie",
        description: "Zaloguj się, aby kontynuować",
        variant: "destructive"
      });
      return;
    }

    if (!instagramLink) {
      toast({
        title: "Brakujące informacje",
        description: "Podaj swoją nazwę użytkownika Instagram",
        variant: "destructive"
      });
      return;
    }

    const instagramUsername = extractInstagramUsername(instagramLink);
    if (!instagramUsername) {
      toast({
        title: "Nieprawidłowa nazwa",
        description: "Podaj prawidłową nazwę użytkownika",
        variant: "destructive"
      });
      return;
    }

    // If the input matches the username the user already verified+confirmed
    // earlier in this onboarding session, skip the round-trip to verify-profile
    // and skip the confirm dialog. We still upsert defensively so the row
    // exists — e.g. after an admin wipe, or if the user landed here from
    // somewhere that never created a profile row.
    const previouslyConfirmed = localStorage.getItem("onboarding_instagram");
    if (previouslyConfirmed && previouslyConfirmed === instagramUsername) {
      const { error: saveError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            user_id: userId,
            instagram_username: previouslyConfirmed,
            onboarding_started: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );
      if (saveError) {
        toast({
          title: "Błąd zapisu",
          description: "Nie udało się zapisać profilu. Spróbuj ponownie.",
          variant: "destructive",
        });
        return;
      }
      navigate("/onboarding/industry");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-profile', {
        body: { 
          user_id: userId,
          instagram_username: instagramUsername 
        }
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data.success) {
        if (data.is_potentially_adult) {
          setValidationError("Ten profil nie może zostać połączony z Viraluj.");
          return;
        }
        if (data.error_type === "invalid_username") {
          setValidationError("Podaj poprawną nazwę użytkownika");
          return;
        }
        toast({
          title: "Błąd weryfikacji",
          description: data.message || "Nie udało się zweryfikować profilu",
          variant: "destructive"
        });
        return;
      }

      if (data.is_private) {
        setValidationError("Ten profil jest prywatny. Zmień go na publiczny, aby kontynuować.");
        return;
      }

      setAvatarError(false);
      const profile: VerifiedProfile = {
        username: data.username,
        full_name: data.full_name,
        profile_pic_url: data.profile_pic_url,
        followers_count: data.followers_count,
        is_verified: data.is_verified,
      };
      setVerifiedProfile(profile);

      // Preload avatar before showing dialog to avoid image pop-in
      if (data.profile_pic_url) {
        const blobUrl = await preloadAuthImage(data.profile_pic_url);
        if (blobUrl) {
          setAvatarBlobUrl(blobUrl);
        } else {
          setAvatarError(true);
        }
        setShowConfirmDialog(true);
      } else {
        setShowConfirmDialog(true);
      }
    } catch (err) {
      const message = logEdgeFunctionError('verify-profile', err, { username: instagramUsername });
      toast({
        title: "Błąd połączenia",
        description: message,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmContinue = async () => {
    if (!userId || !verifiedProfile) return;

    setIsLoading(true);
    try {
      // Use upsert so we handle the race where `markOnboardingStarted`
      // hasn't yet inserted the row. `update` would silently match 0 rows
      // and the username would never persist.
      const { error: saveError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            user_id: userId,
            instagram_username: verifiedProfile.username,
            onboarding_started: true,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (saveError) {
        toast({
          title: "Błąd zapisu",
          description: "Nie udało się zapisać profilu. Spróbuj ponownie.",
          variant: "destructive"
        });
        return;
      }

      localStorage.setItem('onboarding_instagram', verifiedProfile.username);
      localStorage.setItem('onboarding_from_instagram', '1');

      setShowConfirmDialog(false);
      navigate("/onboarding/industry");
    } catch {
      toast({
        title: "Błąd zapisu",
        description: "Nie udało się zapisać profilu. Spróbuj ponownie.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FEF3EC] via-[#F9B896] to-[#F5A07A] relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent" />
      <div className="absolute top-1/4 -right-48 w-96 h-96 bg-primary/10 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute bottom-1/4 -left-48 w-96 h-96 bg-secondary/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />

      <div className="relative z-10 max-w-lg mx-auto px-4 py-8 md:py-16">
        {/* Step indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center gap-2">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                ${step.id === 1
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-white/60 text-gray-500'
                }
              `}>
                {step.id}
              </div>
              {index < STEPS.length - 1 && (
                <div className="w-8 h-0.5 bg-gray-300" />
              )}
            </div>
          ))}
        </div>

        <div className="flex justify-center mb-6">
          <VideoTutorialHint />
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white/80 rounded-2xl shadow-lg border border-white/60 p-6 md:p-8"
        >
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Instagram className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight text-gray-900 mb-2">
              Połącz swój Instagram
            </h1>
            <p className="text-gray-500 leading-relaxed">
              Podaj nazwę profilu, który chcesz rozwijać
            </p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Input
                type="text"
                placeholder="nazwa_profilu lub link"
                value={instagramLink}
                onChange={(e) => setInstagramLink(e.target.value.toLowerCase())}
                className={`h-14 px-4 text-base border-2 rounded-xl transition-all bg-white text-gray-900 placeholder:text-gray-400 font-medium ${
                  instagramLink && isValidUsername(instagramLink)
                    ? 'border-primary bg-primary/5'
                    : instagramLink && validationError
                    ? 'border-destructive'
                    : 'border-gray-200 focus:border-primary'
                }`}
              />
              {instagramLink && isValidUsername(instagramLink) && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Check className="h-4 w-4 text-primary-foreground" />
                  </div>
                </div>
              )}
            </div>

            {validationError ? (
              <p className="text-sm text-destructive text-center">
                {validationError}
              </p>
            ) : (
              <p className="text-sm text-gray-500 text-center">
                Profil musi być publiczny
              </p>
            )}
          </div>

          <Button
            onClick={handleContinueClick}
            disabled={!instagramLink || isLoading || !isValidUsername(instagramLink)}
            className="w-full h-12 text-base font-medium mt-6"
            size="lg"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Weryfikacja...
              </div>
            ) : (
              "Kontynuuj"
            )}
          </Button>
        </motion.div>

      </div>

      {/* Confirmation Dialog with verified profile data */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md bg-white/95 border border-white/70 shadow-2xl rounded-2xl">
          <AlertDialogHeader>
            <div className="flex flex-col items-center gap-3 mb-2">
              {avatarBlobUrl && !avatarError ? (
                <img
                  src={avatarBlobUrl}
                  alt={`@${verifiedProfile.username}`}
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarError(true)}
                  className="w-14 h-14 rounded-full object-cover border-2 border-white/80 shadow-md"
                />
              ) : (
                <div className="w-14 h-14 bg-gradient-to-br from-primary/80 to-primary rounded-full flex items-center justify-center text-white text-xl font-bold shadow-md">
                  {verifiedProfile?.full_name?.charAt(0)?.toUpperCase() || "?"}
                </div>
              )}
            </div>
            <AlertDialogTitle className="text-center text-xl font-semibold tracking-tight text-gray-900">
              Potwierdź profil
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-center space-y-3">
                <div className="bg-white/60 rounded-xl p-4 my-3 border border-white/80 shadow-sm">
                  <div className="flex items-center justify-center gap-2 text-base font-medium text-gray-900">
                    <div className="w-5 h-5 bg-primary/10 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    @{verifiedProfile?.username}
                  </div>
                  {verifiedProfile?.full_name && (
                    <p className="text-sm text-gray-500 mt-1">
                      {verifiedProfile.full_name}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {formatFollowerCount(verifiedProfile?.followers_count ?? 0)} obserwujących
                  </p>
                </div>
                <p className="text-sm text-gray-500">
                  Czy to Twój profil Instagram?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2 mt-2 sm:!justify-between sm:!space-x-0">
            <AlertDialogCancel className="w-full sm:w-auto rounded-xl bg-white/60 border border-gray-200/60 text-gray-700 hover:bg-primary/10 hover:border-primary/30 hover:text-primary shadow-sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Popraw
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmContinue}
              disabled={isLoading}
              className="w-full sm:w-auto rounded-xl shadow-sm"
            >
              Tak, kontynuuj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InstagramSetup;
